// The address half of a search: fetched, grouped, and safe against reordering.
//
// `/api/search/address` is asked once per settled term. Two things make that
// safe. The term arrives debounced, so typing does not fire a request per
// keystroke; and every request takes a monotonic ticket, so a slow answer for
// an older term is dropped instead of overwriting the rows of the newer term
// the operator is now looking at.
//
// The composable owns the request, the rows, the groups and the loading/error
// state, and knows nothing about what the rows mean: callers render `groups`
// and decide what selecting a row does. The error is a plain string rather than
// a thrown error or a failed group, because a failed address request must not
// make the box or the list unusable — the pane shows the message through its
// toast and the next keystroke tries again.
import { computed, ref, watch, type ComputedRef, type Ref } from "vue";

import { apiClient } from "@/client";
import type { AddressSearchResult } from "@/type/api";
import { addressGroups } from "./addressGroups";
import type { SearchGroup } from "./types";

export interface AddressSearchOptions {
	/** Trimmed terms shorter than this count as empty: the rows clear and no
	 * request is made. Callers that debounce through `useDebouncedTerm` already
	 * enforce their own minimum; this is the composable's own floor, so a bare
	 * term ref cannot ask the geocoder about a single character. */
	minimumLength?: number;
}

export interface AddressSearch {
	results: Ref<AddressSearchResult[]>;
	groups: ComputedRef<SearchGroup<AddressSearchResult>[]>;
	loading: Ref<boolean>;
	error: Ref<string | null>;
}

export function useAddressSearch(
	term: Ref<string>,
	options: AddressSearchOptions = {},
): AddressSearch {
	const minimumLength = options.minimumLength ?? 1;
	const results = ref<AddressSearchResult[]>([]);
	const loading = ref(false);
	const error = ref<string | null>(null);
	const groups = computed(() => addressGroups(results.value));

	// The ticket of the newest request. An answer whose ticket is no longer the
	// current one belongs to a term the operator has already replaced.
	let sequence = 0;

	watch(
		term,
		async (value) => {
			const query = value.trim();
			if (query.length < minimumLength) {
				// Bumping the ticket out from under an in-flight request is what
				// cancels it: its answer arrives stale and is ignored below.
				sequence += 1;
				results.value = [];
				loading.value = false;
				error.value = null;
				return;
			}
			const ticket = ++sequence;
			loading.value = true;
			error.value = null;
			try {
				const hits = await apiClient.JSONGet<AddressSearchResult[]>(
					`/api/search/address?q=${encodeURIComponent(query)}`,
				);
				if (ticket !== sequence) return;
				results.value = hits;
			} catch (e: unknown) {
				if (ticket !== sequence) return;
				results.value = [];
				error.value =
					e && typeof e === "object" && "message" in e
						? String((e as { message: unknown }).message)
						: "Failed to search addresses.";
			} finally {
				// Only the newest request owns the spinner: a dropped answer must
				// not clear the loading state of the request that replaced it.
				if (ticket === sequence) loading.value = false;
			}
		},
		{ immediate: true },
	);

	return { results, groups, loading, error };
}
